import React, { useState } from "react";
import { Box, IconButton, Tab, Tabs, CircularProgress, Tooltip } from "@mui/material";
import { Download, AutoAwesome, OpenInNew } from "@mui/icons-material";

const DashboardMiniTable = ({ groupTitle, configs = [] }) => {
  const [activeTab, setActiveTab] = useState(0);

  if (!configs || configs.length === 0) return null;
  
  const current = configs[activeTab];

  const brandingColor = current.accentColor || "#0f172a";

  return (
    <Box
      className="flex flex-col h-full bg-white transition-all duration-300"
      sx={{
        borderRadius: "14px", 
        overflow: "hidden",
        border: "1px solid #cbd5e1", 
        boxShadow: "0 4px 24px -6px rgba(148, 163, 184, 0.12), 0 2px 8px -2px rgba(148, 163, 184, 0.06)"
      }}
    >
      {/* HEADER SECTION */}
      <div className="px-6 py-4 flex items-center justify-between bg-slate-50 border-b border-slate-300">
        <div className="flex items-center gap-3">
          {current.accentColor && (
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-white border border-slate-200 shadow-sm"
              style={{
                boxShadow: `0 2px 8px ${current.accentColor}15`
              }}
            >
              <AutoAwesome style={{ color: current.accentColor, fontSize: 15 }} />
            </div>
          )}
          <div>
            {groupTitle && (
              <span className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-0.5">
                {groupTitle}
              </span>
            )}
            <h2 className="text-base font-bold text-slate-800 tracking-tight">
              {current.title}
            </h2>
          </div>
        </div>

        {/* COMPACT TOP ACTIONS */}
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center px-2.5 py-1 text-xs font-bold font-mono text-slate-700 bg-white border border-slate-300 rounded-lg shadow-sm">
            {current.data?.length || 0} items
          </span>

          {current.onPreview && (
            <Tooltip title="Open Interactive View" arrow>
              <IconButton
                onClick={current.onPreview}
                size="small"
                sx={{
                  width: '32px',
                  height: '32px',
                  color: '#475569',
                  bgcolor: '#fff',
                  borderRadius: '10px',
                  border: '1px solid #cbd5e1',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  transition: 'all 0.15s ease',
                  '&:hover': { bgcolor: '#f1f5f9', color: brandingColor, borderColor: '#94a3b8', transform: 'translateY(-1px)' }
                }}
              >
                <OpenInNew sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}

          {current.onDownload && (
            <Tooltip title="Export to Spreadsheet" arrow>
              <IconButton
                onClick={current.onDownload}
                size="small"
                sx={{
                  width: '32px',
                  height: '32px',
                  color: '#475569',
                  bgcolor: '#fff',
                  borderRadius: '10px',
                  border: '1px solid #cbd5e1',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  transition: 'all 0.15s ease',
                  '&:hover': { bgcolor: '#f1f5f9', color: brandingColor, borderColor: '#94a3b8', transform: 'translateY(-1px)' }
                }}
              >
                <Download sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </div>

      {/* SEGMENT CONTROLLER */}
      <div className="px-6 py-2.5 bg-white border-b border-slate-200">
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons={false}
          sx={{
            minHeight: '34px',
            '& .MuiTabs-indicator': { display: 'none' },
          }}
        >
          {configs.map((cfg, i) => {
            const isSelected = i === activeTab;
            return (
              <Tab
                key={i}
                label={cfg.tabLabel || cfg.title}
                sx={{
                  minHeight: '34px',
                  padding: '6px 16px',
                  textTransform: 'none',
                  fontSize: '12px',
                  fontWeight: isSelected ? 700 : 500,
                  borderRadius: '8px',
                  marginRight: '8px',
                  color: isSelected ? '#ffffff' : '#475569',
                  backgroundColor: isSelected ? brandingColor : '#f1f5f9',
                  border: '1px solid',
                  borderColor: isSelected ? 'transparent' : '#e2e8f0',
                  boxShadow: isSelected ? `0 4px 12px ${brandingColor}25` : 'none',
                  minWidth: 'auto',
                  transition: 'all 0.2s ease',
                  '&.Mui-selected': {
                    color: '#fff !important',
                  },
                  '&:hover': {
                    backgroundColor: isSelected ? brandingColor : '#e2e8f0',
                    color: isSelected ? '#fff' : '#1e293b'
                  }
                }}
              />
            );
          })}
        </Tabs>
      </div>

      {/* SOLID DATA MATRIX */}
      <div className="flex-grow overflow-hidden bg-slate-100">
        <div style={{ maxHeight: "380px", overflowY: "auto" }}>
          <table className="w-full border-collapse text-left bg-white">
            <thead>
              <tr className="bg-slate-100 sticky top-0 z-10 border-b-2 border-slate-300 shadow-sm">
                {current.columns.map((col, idx) => (
                  <th 
                    key={idx} 
                    className="px-5 py-3 text-xs font-bold text-slate-700 uppercase tracking-wider border-r border-slate-200 last:border-r-0"
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {current.loading ? (
                <tr>
                  <td colSpan={current.columns.length} className="py-24 text-center bg-white">
                    <CircularProgress size={24} thickness={4} sx={{ color: brandingColor }} />
                  </td>
                </tr>
              ) : current.data?.length > 0 ? (
                current.data.map((row, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className={`group border-b border-slate-200 last:border-b-0 transition-colors duration-100 cursor-pointer 
                      ${rowIndex % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'} 
                      hover:bg-slate-100/70`}
                    onClick={current.onPreview}
                  >
                    {current.columns.map((col, colIdx) => {
                      // Production-safe label evaluation logic to prevent crash
                      let isSerialNo = colIdx === 0; // fallback default
                      if (col && col.label) {
                        const labelString = typeof col.label === "string" ? col.label : String(col.label);
                        const labelLower = labelString.toLowerCase();
                        isSerialNo = labelLower.includes('s.') || 
                                     labelLower.includes('sr') || 
                                     labelLower.includes('id') || 
                                     labelLower.includes('serial') || 
                                     colIdx === 0;
                      }

                      return (
                        <td
                          key={colIdx}
                          className={`px-5 py-3 text-sm border-r border-slate-200 last:border-r-0 truncate max-w-[200px] transition-colors duration-150
                            ${isSerialNo 
                              ? 'text-slate-900 font-bold font-mono bg-slate-100/30 group-hover:bg-slate-200/40' 
                              : 'text-slate-600 font-medium group-hover:text-slate-900'
                            }`}
                        >
                          {col.render ? col.render(row, rowIndex) : row[col.key]}
                        </td>
                      );
                    })}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={current.columns.length} className="py-16 text-center bg-white">
                    <span className="text-sm font-semibold text-slate-400">No records available</span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Box>
  );
};

export default DashboardMiniTable;